<script setup>
    import InputError from '@/Components/InputError.vue'
    import PrimaryButton from '@/Components/PrimaryButton.vue'
    import TextInput from '@/Components/TextInput.vue'
    import Checkbox from '@/Components/Checkbox.vue'
    import { Link, useForm, usePage } from '@inertiajs/vue3'
    import { ref, onMounted, watch, computed } from 'vue'
    import ApplicationLogo from '@/Components/ApplicationLogo.vue'
    import Toast from '@/Components/Toast.vue'
    import Modal from '@/Components/Modal.vue'
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
    import { faXmark } from '@fortawesome/free-solid-svg-icons'

    const props = defineProps({
        showRegisterFirst: {
            type: Boolean,
            default: false,
        },
        canResetPassword: {
            type: Boolean,
            default: false,
        },
        status: {
            type: String,
            default: '',
        },
        show: {
            type: Boolean,
            default: false,
        },
    })

    const emit = defineEmits(['close'])

    const handleClose = () => {
        emit('close')
    }

    const showRegisterForm = ref(props.showRegisterFirst)
    const isFormValid = ref(false)
    const isLoginValid = ref(false)
    const showPassword = ref(false)
    const showLoginPassword = ref(false)

    const showBannedToast = ref(false)
    const bannedToastText = ref('Your account has been locked, please contact Customer Support.')

    const optPrompt = ref(false)

    const form = useForm({
        email: '',
        profile_url: '',
        name: '',
        password: '',
        password_confirmation: '',
        email_monthly_newsletter: false,
        tos: false,
        creator_signup: false,
        conversion_user_id: null,
        float_user_id: null,
        intended: '',
    })

    const loginForm = useForm({
        email: '',
        password: '',
        remember: true,
        otp: '',
        intended: '',
    })

    const backref = computed(() => usePage().props.backref ?? '')
    const comeFromMessage = computed(() => backref.value?.includes?.('message'))
    const comeFromMessageNav = computed(() => backref.value?.includes?.('nav'))

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const canShowPasswordField = computed(() => {
        return (
            form.profile_url.trim() !== '' &&
            form.email.trim() !== '' &&
            emailRegex.test(form.email.trim())
        )
    })

    const shouldShowPasswordField = ref(false)

    watch(canShowPasswordField, newValue => {
        if (newValue) {
            // Add a delay to prevent iOS Safari from detecting the field too early
            setTimeout(() => {
                shouldShowPasswordField.value = true
            }, 500)
        } else {
            shouldShowPasswordField.value = false
        }
    })

    const loginSubmit = () => {
        const email = (loginForm.email ?? '').trim()
        if (!emailRegex.test(email)) {
            loginForm.setError?.('email', 'Please enter your email address.')
            return
        }
        loginForm.clearErrors?.('email')

        loginForm.post(route('login'), {
            onFinish: () => loginForm.reset('password'),
            onError: error => {
                if (usePage().props.errors?.otp) {
                    optPrompt.value = true
                }
                form.errors.tos = error.email
            },
        })
    }

    const submit = () => {
        if (!form.tos) {
            form.errors.tos = 'Please accept terms & conditions'
            return
        }
        form.errors.tos = ''

        // Keep backend expectations intact: submit display name as the display url
        form.name = (form.profile_url ?? '').trim()

        form.post(route('register'), {
            onFinish: () => {
                form.reset('password', 'password_confirmation')
                const hasFathom =
                    typeof window !== 'undefined' && typeof window.fathom !== 'undefined'
                if (hasFathom) {
                    if (comeFromMessage.value && !comeFromMessageNav.value) {
                        window.fathom.trackEvent('exn_lander_signup_completed')
                    } else if (comeFromMessageNav.value && !comeFromMessage.value) {
                        window.fathom.trackEvent('exn_lander_nav_signup_completed')
                    }
                }
            },
        })
    }

    const updateIntended = () => {
        if (typeof window !== 'undefined') {
            const currentUrl = window.location.href
            loginForm.intended = currentUrl
            form.intended = currentUrl
        }
    }

    onMounted(() => {
        const urlParams = new URLSearchParams(window.location.search)

        if (urlParams.get('type') === 'join') {
            showRegisterForm.value = true
        }

        if (urlParams.get('banned') === '1') {
            showBannedToast.value = true
        }

        form.conversion_user_id = urlParams.get('conversion_user_id')
        form.float_user_id = urlParams.get('float_user_id')
        updateIntended()
    })

    watch(
        () => [form.profile_url, form.email, form.password],
        () => {
            isFormValid.value =
                form.profile_url.trim() !== '' &&
                form.email.trim() !== '' &&
                form.password.trim() !== '' &&
                emailRegex.test(form.email.trim())
        },
    )

    watch(
        () => [form.profile_url],
        () => {
            // Keep Display Name in sync with Display URL
            form.name = (form.profile_url ?? '').trim()

            // Clear errors for either field since they're effectively the same input now
            form.clearErrors?.('profile_url')
            form.clearErrors?.('name')
        },
    )

    watch(
        () => [loginForm.email, loginForm.password],
        () => {
            isLoginValid.value =
                emailRegex.test(loginForm.email.trim()) && loginForm.password.trim() !== ''
        },
    )

    watch(
        () => props.show,
        value => {
            if (value) {
                updateIntended()
            }
        },
    )

    // Ensure the correct tab is shown each time the modal opens (e.g. "Join Free" should land on Sign Up)
    watch(
        () => [props.show, props.showRegisterFirst],
        ([show, showRegisterFirst]) => {
            if (show) {
                showRegisterForm.value = !!showRegisterFirst
            }
        },
    )

    const closeButton = () => {
        showBannedToast.value = false
    }
</script>

<template>
    <Modal
        :show="show"
        @close="handleClose"
        max-width="lg"
        :is-blured="true"
        class-name="bg-[#111113] text-white p-0 max-h-[80vh] overflow-y-auto">
        <div class="relative">
            <div class="access-modal dark relative">
                <div class="access-modal__container">
                    <div class="access-modal__panel">
                        <button
                            @click="handleClose"
                            type="button"
                            class="absolute right-4 top-4 z-[1000] inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 hover:text-white">
                            <FontAwesomeIcon :icon="faXmark" class="h-4 w-4" aria-hidden="true" />
                            <span class="sr-only">Close modal</span>
                        </button>
                        <div class="access-modal__logo">
                            <Link :href="route('home')">
                                <ApplicationLogo id="logo" />
                            </Link>
                        </div>

                        <ul class="access-modal__tabs">
                            <li class="w-full">
                                <a
                                    href="#"
                                    :class="{ active: showRegisterForm }"
                                    class="access-modal__tab"
                                    aria-current="page"
                                    @click.prevent="showRegisterForm = true"
                                    >Sign Up</a
                                >
                            </li>
                            <li class="w-full">
                                <a
                                    href="#"
                                    :class="{ active: !showRegisterForm }"
                                    class="access-modal__tab"
                                    @click.prevent="showRegisterForm = false"
                                    >Log In</a
                                >
                            </li>
                        </ul>

                        <form
                            class="access-modal__form"
                            @submit.prevent="submit"
                            v-if="showRegisterForm">
                            <div class="relative">
                                <label class="access-modal__label">Email</label>
                                <TextInput
                                    id="signup-email"
                                    type="email"
                                    class="access-field"
                                    v-model="form.email"
                                    required
                                    name="email"
                                    autocomplete="username"
                                    inputmode="email"
                                    autocapitalize="off"
                                    autocorrect="off"
                                    placeholder="e.g. Janedow@fyf.com" />
                                <InputError class="mt-2" :message="form.errors.email" />
                            </div>

                            <!-- Display Name is derived from Display URL; keep it in the payload but hide the field -->
                            <input id="signup-name" type="hidden" name="name" v-model="form.name" />

                            <div class="relative">
                                <label class="access-modal__label">Display URL</label>
                                <TextInput
                                    id="signup-profile"
                                    type="text"
                                    class="access-field"
                                    v-model="form.profile_url"
                                    required
                                    name="profile_url"
                                    autocomplete="nickname"
                                    data-lpignore="true"
                                    data-1p-ignore="true"
                                    data-bwignore="true"
                                    placeholder="e.g. Bluesky" />
                                <InputError
                                    class="mt-2"
                                    :message="form.errors.profile_url || form.errors.name" />
                            </div>

                            <div class="relative">
                                <label class="access-modal__label">Password</label>
                                <TextInput
                                    id="signup-password"
                                    :type="showPassword ? 'text' : 'password'"
                                    class="access-field"
                                    v-model="form.password"
                                    required
                                    name="password"
                                    autocomplete="new-password"
                                    autocorrect="off"
                                    autocapitalize="off"
                                    data-form-type="register"
                                    placeholder="Password" />
                                <InputError class="mt-2" :message="form.errors.password" />
                            </div>

                            <div v-if="shouldShowPasswordField" class="mt-0 first:block">
                                <label class="flex items-center">
                                    <Checkbox
                                        name="show_password"
                                        v-model="showPassword"
                                        class="access-checkbox" />
                                    <span class="checkmark-text ml-2 text-sm">Show password</span>
                                </label>
                            </div>

                            <div class="mt-0 block">
                                <label class="flex items-center">
                                    <Checkbox
                                        name="email_monthly_newsletter"
                                        v-model="form.email_monthly_newsletter"
                                        class="access-checkbox" />
                                    <span class="checkmark-text ml-2 text-sm">
                                        Yes, I want to receive the newsletter!
                                    </span>
                                </label>
                            </div>

                            <div class="mt-0 block">
                                <label class="flex items-center">
                                    <Checkbox
                                        v-model="form.tos"
                                        name="tos"
                                        class="access-checkbox" />
                                    <span class="checkmark-text ml-2 text-sm">
                                        I agree with the
                                        <a
                                            :href="route('tos')"
                                            class="text-mintgreen"
                                            target="_blank">
                                            terms & conditions
                                        </a>
                                    </span>
                                </label>
                                <InputError
                                    v-if="loginForm.errors.email && !showRegisterForm"
                                    class="li mt-2"
                                    :message="form.errors.tos" />
                            </div>

                            <div
                                class="mt-4 flex flex-col items-center justify-end space-y-1 sm:space-y-5">
                                <PrimaryButton
                                    id="signup-button"
                                    class="no-opacity-change access-button"
                                    :class="{
                                        'opacity-25': form.processing,
                                        'is-valid': isFormValid,
                                        'is-inactive': !isFormValid,
                                    }"
                                    :disabled="form.processing">
                                    Sign Up
                                </PrimaryButton>

                                <Link
                                    id="has-account"
                                    :href="route('login')"
                                    class="text-sm text-[#65e8a4] hover:text-[#41966a]">
                                    I already have an account
                                </Link>
                            </div>
                        </form>

                        <form
                            class="access-modal__form"
                            @submit.prevent="loginSubmit"
                            v-if="!showRegisterForm">
                            <div class="relative">
                                <label class="access-modal__label">Email</label>
                                <TextInput
                                    id="login-email"
                                    type="email"
                                    class="access-field"
                                    v-model="loginForm.email"
                                    required
                                    name="email"
                                    autocomplete="username"
                                    inputmode="email"
                                    autocapitalize="off"
                                    autocorrect="off"
                                    placeholder="e.g. Janedow@fyf.com" />
                                <InputError
                                    v-if="loginForm.errors.email && !showRegisterForm"
                                    class="su mt-2"
                                    :message="loginForm.errors.email" />
                            </div>

                            <div class="relative mt-4">
                                <label class="access-modal__label">Password</label>
                                <TextInput
                                    id="login-password"
                                    :type="showLoginPassword ? 'text' : 'password'"
                                    class="access-field"
                                    v-model="loginForm.password"
                                    required
                                    name="password"
                                    autocomplete="current-password"
                                    placeholder="Password" />
                                <InputError
                                    v-if="loginForm.errors.password"
                                    class="mt-2"
                                    :message="loginForm.errors.password" />
                            </div>

                            <div v-if="optPrompt" class="mt-4">
                                <TextInput
                                    id="otp"
                                    type="text"
                                    class="access-field"
                                    v-model="loginForm.otp"
                                    required
                                    autocomplete="otp"
                                    placeholder="Verification Code" />
                                <InputError
                                    v-if="loginForm.errors.otp"
                                    class="mt-2"
                                    :message="loginForm.errors.otp" />
                            </div>

                            <div class="flex justify-between">
                                <div class="mt-4 block">
                                    <label class="flex items-center">
                                        <Checkbox
                                            class="access-checkbox"
                                            name="remember"
                                            v-model:checked="loginForm.remember" />
                                        <span class="checkmark-text ml-2 text-sm">Remember me</span>
                                    </label>
                                </div>
                                <div class="mt-4 block">
                                    <label class="flex items-center">
                                        <Checkbox
                                            class="access-checkbox"
                                            name="show_login_password"
                                            v-model="showLoginPassword" />
                                        <span class="checkmark-text ml-2 text-sm"
                                            >Show Password</span
                                        >
                                    </label>
                                </div>
                            </div>

                            <div
                                class="mt-4 flex flex-col items-end justify-end space-y-1 sm:space-y-5">
                                <PrimaryButton
                                    id="login-button"
                                    class="access-button"
                                    :class="{
                                        'is-valid': isLoginValid,
                                        'is-inactive': !isLoginValid,
                                    }"
                                    :disabled="loginForm.processing">
                                    Log in
                                </PrimaryButton>
                                <Link
                                    id="forgot-password"
                                    v-if="canResetPassword"
                                    :href="route('password.request')"
                                    class="self-center text-sm text-[#65e8a4] underline hover:text-[#41966a] sm:self-end">
                                    Forgot your password?
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <toast
                :show="showBannedToast"
                transitionName="slide-fade"
                :text="bannedToastText"
                :transitionEnter="300"
                :transitionLeave="500"
                position="bottomLeft"
                @closeButton="closeButton" />
        </div>
    </Modal>
</template>

<style scoped>
    .access-modal {
        display: flex;
        width: 100%;
        justify-content: center;
        align-items: center;

        background: radial-gradient(circle at center, rgba(101, 232, 164, 0.05), transparent);
    }

    .access-modal__container {
        display: flex;
        flex-direction: column;
        align-items: center;

        width: 100%;
    }

    .access-modal__panel {
        width: 100%;
        border: 1px solid #363636;
        border-radius: 10px;
        background-color: #0a0a0a;
        padding: 1rem 1.25rem 2rem;
    }

    .access-modal__logo {
        display: flex;
        justify-content: center;
        margin: 1.5rem 0;
    }

    .access-modal__tabs {
        display: flex;
        text-align: center;
        font-size: 0.875rem;
        font-weight: 500;
        color: #e5e7eb;
    }

    .access-modal__tab {
        display: flex;
        width: 100%;
        justify-content: center;
        align-items: center;
        border-bottom: 2px solid #444444;
        padding: 0.5rem 2rem 1rem;
        font-size: 21px;
        color: inherit;
    }

    .access-modal__form {
        margin-top: 2.5rem;
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
    }

    .access-modal__label {
        position: absolute;
        left: 1.25rem;
        top: -10px;
        padding: 0 0.5rem;
        background-color: #161616;
        font-size: 12px;
        color: #ccc;
    }

    .access-field {
        margin-top: 0.5rem;
        display: block;
        width: 100%;
        border-radius: 10px;
        border: 1px solid #444444;
        background-color: #161616;
        color: #efefef;
        padding: 0.75rem 1rem;
    }

    .access-field:focus {
        border-color: #65e8a4;
        box-shadow: none;
    }

    .access-checkbox {
        border: 1px solid #444444;
        background-color: #161616;
    }

    .access-button {
        display: flex;
        width: 100%;
        justify-content: center;
        padding: 1rem;
        border-radius: 25px;
        border: 1px solid #444444;
        background-color: #161616;
        color: #444444;
        font-size: 16px;
        text-transform: capitalize;
    }

    .active {
        border-color: #65e8a4 !important;
        color: #ffffff !important;
    }

    .is-valid {
        background-color: #0a0a0a;
        border-color: #65e8a4;
        color: #65e8a4;
    }

    .is-valid:hover {
        background-color: #65e8a4;
        color: #000;
    }

    .is-inactive,
    button.is-inactive:hover {
        border-color: #444;
        color: #444;
        background-color: #0a0a0a;
    }

    .no-opacity-change {
        opacity: 1;
    }

    #logo {
        margin: 0;
    }

    @media (max-width: 480px) {
        .access-modal__panel {
            padding: 0.75rem 1rem 2rem;
        }

        .access-modal__tab {
            font-size: 18px;
            padding: 0.5rem 1rem 1rem;
        }

        .access-modal__form {
            padding-bottom: 1rem;
        }
    }
</style>
